import type { ReactElement, CSSProperties } from 'react';
import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import { IOSFullscreen } from '@pp/core/src/components/fullscreen/IOSFullscreen';
import { useResizeEffect } from '@pp/core/src/hooks/useResizeEffect';
import { IFRAME_TYPE } from '@pp/core/src/store/slices/launcher/types';
import { useIsLandscape, useIsSmallerThanWide } from '@pp/core/src/styles/hooks/media';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { launcherContextActions } from '../../context/actions';
import { useLauncherContext } from '../../context/LauncherContext';
import { LauncherCommunicator } from '../../controllers/LauncherCommunicationController';
import { useLayoutScale } from '../../helpers/hooks/useLayoutScale';

import { DrawerWrapper } from './DrawerWrapper';
import { Table } from './Table';

import styles from './LauncherLayout.module.css';

export const LauncherLayout = (): ReactElement => {
  const { tables, dispatch, showFullscreen } = useLauncherContext();
  const layoutRef = useRef<HTMLDivElement>(null);
  const [layoutScale, tableScale, isMobile] = useLayoutScale(layoutRef);

  const hasMTB = tables.some((table) => table.iframeType === IFRAME_TYPE.MTB);
  const isLandscape = useIsLandscape();
  const isSmallerThanWide = useIsSmallerThanWide();

  const hideMtb = isLandscape && isSmallerThanWide;

  const layoutTables = useModifiers(
    styles,
    'layout',
    {
      [tables.length]: true,
      scaled: tables.length > 2,
      with_mtb: hasMTB,
      desktopMtb: !isMobile && tables[1]?.isMtbMinimized,
      mobile: isMobile,
      hideMtb,
    },
    { withBaseClass: true }
  );

  const handleTableClose = useCallback(
    (tableId: string) => {
      LauncherCommunicator.removeTarget(tableId);
      dispatch(launcherContextActions.removeTable({ tableId }));
    },
    [dispatch]
  );

  const resizeHandler = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const mtbTable = tables.find((t) => t.iframeType === IFRAME_TYPE.MTB);
    if (!mtbTable) return;

    const isInGameDesktop = width >= 1024 && height > 500;
    const isMobileOrLandscapeNow = width < 1024 || height <= 500;

    const prev = (window as any).__prevResizeState;

    if (!prev) {
      (window as any).__prevResizeState = { isInGameDesktop, isMobileOrLandscapeNow };
      return;
    }

    (window as any).__prevResizeState = { isInGameDesktop, isMobileOrLandscapeNow };

    if (prev.isMobileOrLandscapeNow !== isMobileOrLandscapeNow || prev.isInGameDesktop !== isInGameDesktop) {
      clearTimeout((window as any).__resizeTimer);
      (window as any).__resizeTimer = setTimeout(() => {
        if ((prev.isMobileOrLandscapeNow && isInGameDesktop) || (prev.isInGameDesktop && isMobileOrLandscapeNow)) {
          handleTableClose(mtbTable.tableId);
        }
      }, 300);
    }
  }, [tables, handleTableClose]);
  useResizeEffect(resizeHandler, 200);

  const renderTables = useCallback(() => {
    return tables.map((table) => {
      if (isMobile && table.iframeType === IFRAME_TYPE.MTB) {
        return (
          <DrawerWrapper
            key="mtb-drawer"
            table={table}
            isLandscape={isLandscape}
            onClose={() => handleTableClose(table.tableId)}
          />
        );
      }

      const itemMods = useModifiers(
        styles,
        'item',
        {
          mtb: table.iframeType === IFRAME_TYPE.MTB,
          minimize: table.isMtbMinimized,
          hideMtb: table.iframeType === IFRAME_TYPE.MTB && hideMtb,
        },
        { withBaseClass: true }
      );
      return (
        <div key={table.tableId} className={itemMods}>
          <Table
            table={table}
            isMobile={isMobile}
            onTableClose={handleTableClose}
            tablesCount={tables.length}
            hasMTB={hasMTB}
          />
        </div>
      );
    });
  }, [tables, isLandscape, hasMTB, hideMtb, isMobile, handleTableClose]);

  return (
    <div
      className={layoutTables}
      style={{ '--launcherScale': layoutScale, '--tableScale': tableScale } as CSSProperties}
      ref={layoutRef}
    >
      {showFullscreen && createPortal(<IOSFullscreen isReadyToRender />, document.getElementById('root') as HTMLElement)}
      {renderTables()}
    </div>
  );
};
